/**
 * An arm, as people read it: model and effort first, then a badge for the production setup
 * or a draft prompt, then the prompt hash. The arm ID says the same in one string (it is
 * made from the setup), so it is only in the tooltip, for talking to Claude.
 */
import type { EvalModel, RunEvalArmInfo } from '../types/evalTypes';
import { isProductionArm, modelLabel } from '../utils/evalFormat';

interface ArmLabelProps {
  arm: RunEvalArmInfo;
  models: EvalModel[];
  /** The suite's registry model, for the production badge. */
  productionModel: string | undefined;
  /** One line, for table headers. */
  compact?: boolean;
  /** Makes the draft badge a button that shows the draft prompt. */
  onShowDraft?: () => void;
}

const badgeClass = 'text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap';

export const ArmLabel = ({ arm, models, productionModel, compact = false, onShowDraft }: ArmLabelProps) => {
  // A model that takes effort but got none runs at its default; one that takes none has no effort to show.
  const takesEffort = (models.find((model) => model.id === arm.model)?.efforts.length ?? 0) > 0;
  const effort = arm.effort ?? (takesEffort ? 'default effort' : undefined);
  const setup = [modelLabel(models, arm.model), effort].filter(Boolean).join(' · ');
  const draftBadge = onShowDraft ? (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onShowDraft();
      }}
      className={`${badgeClass} bg-purple-100 text-purple-800 hover:bg-purple-200`}
      title="View the draft prompt"
    >
      draft
    </button>
  ) : (
    <span className={`${badgeClass} bg-purple-100 text-purple-800`}>draft</span>
  );

  return (
    <span className={compact ? 'inline-flex flex-wrap items-center justify-center gap-1' : 'inline-flex flex-col'} title={`Arm ${arm.id}, prompt ${arm.promptHash}`}>
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span className="font-medium text-gray-900">{setup}</span>
        {arm.hasPromptOverride ? draftBadge : isProductionArm(arm, productionModel) && <span className={`${badgeClass} bg-indigo-100 text-indigo-800`}>production</span>}
      </span>
      {!compact && <span className="font-mono text-[11px] text-gray-500">prompt {arm.promptHash.slice(0, 6)}</span>}
    </span>
  );
};
